import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import api from '../utils/api';
import PremiumLayout from '../components/layout/PremiumLayout';

// Componenta principală Admin Dashboard - layout premium aliniat cu OP/Agent
export default function AdminDashboard() {
  const location = useLocation();
  const title = getAdminPageTitle(location.pathname);
  const subtitle = 'Lead Platform · roasigur.ro';

  return (
    <PremiumLayout title={title} subtitle={subtitle} variant="admin">
      <Routes>
        <Route path="/" element={<DashboardHome />} />
        <Route path="/ops" element={<OPsList />} />
        <Route path="/agenti" element={<AgentiList />} />
        <Route path="/landing-pages" element={<LandingPagesList />} />
        <Route path="/leads" element={<LeadsList />} />
      </Routes>
    </PremiumLayout>
  );
}

function getAdminPageTitle(pathname) {
  if (pathname === '/admin' || pathname === '/admin/') return 'Dashboard';
  if (pathname === '/admin/ops') return 'Management Roluri';
  if (pathname === '/admin/agenti') return 'Agenți';
  if (pathname === '/admin/landing-pages') return 'Landing Pages';
  if (pathname === '/admin/leads') return 'Lead-uri';
  return 'Admin';
}

// ============================================
// DASHBOARD HOME
// ============================================

function DashboardHome() {
  const [stats, setStats] = useState({
    ops: 0,
    agenti: 0,
    leads: 0,
    landingPages: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Eroare încărcare statistici:', error);
    }
  };

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Dashboard</h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard icon="🏢" label="Roluri" value={stats.ops} color="blue" />
        <StatCard icon="👥" label="Agenți" value={stats.agenti} color="green" />
        <StatCard icon="📝" label="Lead-uri" value={stats.leads} color="purple" />
        <StatCard icon="🌐" label="Landing Pages" value={stats.landingPages} color="orange" />
      </div>

      {/* Welcome Message */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Bun venit în Lead Platform!</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">Backend funcționează perfect. Următorii pași:</p>
        <ul className="list-disc list-inside text-sm text-gray-500 dark:text-gray-400">
          <li>Crează roluri (meniul „Management Roluri”)</li>
          <li>Configurează Landing Pages</li>
          <li>Monitorizează lead-urile</li>
        </ul>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500'
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 p-6 transition-shadow hover:shadow-lg">
      <div className="flex items-center">
        <div className={`${colorClasses[color]} rounded-full p-3 text-white text-2xl mr-4`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  );
}

// Badge pentru rol ierarhic (CSO, Rețea, Regiune, Manager, OP) – Light + Dark
function RoleBadge({ role }) {
  const r = (role || 'op').toLowerCase();
  const config = {
    cso:     { label: 'CSO',      className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
    retea:   { label: 'Rețea',    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
    regiune: { label: 'Regiune',  className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300' },
    manager: { label: 'Manager',   className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
    op:      { label: 'OP',       className: 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-200' }
  };
  const { label, className } = config[r] || config.op;
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${className}`}>
      {label}
    </span>
  );
}

// ============================================
// OPS LIST
// ============================================

function OPsList() {
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editOp, setEditOp] = useState(null);

  useEffect(() => {
    fetchOPs();
  }, []);

  const fetchOPs = async () => {
    try {
      const response = await api.get('/admin/ops');
      setOps(response.data.ops || []);
    } catch (error) {
      console.error('Eroare încărcare OP-uri:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, nume) => {
    if (window.confirm(`Ești sigur că vrei să ștergi operatorul "${nume}"? Această acțiune este ireversibilă și va șterge toate datele asociate (mai puțin lead-urile care trebuie șterse manual înainte).`)) {
      try {
        await api.delete(`/admin/ops/${id}`);
        setOps(ops.filter(op => op.id !== id));
      } catch (error) {
        console.error('Eroare la ștergere:', error);
        alert(error.response?.data?.error || 'Eroare la ștergerea operatorului');
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 p-12 text-center">
        <div className="inline-block w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Se încarcă rolurile...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Management Roluri</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2.5 rounded-xl font-medium shadow-sm hover:bg-blue-700 hover:shadow transition-all"
        >
          + Adaugă Rol
        </button>
      </div>

      {ops.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-8 text-center">
          <div className="text-6xl mb-4">🏢</div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Nu există roluri încă.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-medium shadow-sm hover:bg-blue-700 hover:shadow transition-all"
          >
            Adaugă primul Rol
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nume</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rol</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Agenți</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Lead-uri</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">DPA</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acțiuni</th>
                </tr>
              </thead>
<tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-slate-700">
                  {ops.map((op) => (
                    <tr key={op.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{op.numeComplet}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{op.username}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <RoleBadge role={op.role} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{op.email}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {op._count?.agenti || 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {op._count?.leads || 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${op.acceptatDPA
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                        {op.acceptatDPA ? 'Acceptat' : 'Neacceptat'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="flex items-center gap-3 justify-end">
                        <button
                          type="button"
                          onClick={() => setEditOp(op)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
                          title="Editează"
                        >
                          Editează
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(op.id, op.numeComplet)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors"
                          title="Șterge Operator"
                        >
                          Șterge
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAddModal && <AddOPModal onClose={() => setShowAddModal(false)} onSuccess={fetchOPs} ops={ops} />}
      {editOp && <EditOPModal op={editOp} ops={ops} onClose={() => setEditOp(null)} onSuccess={() => { fetchOPs(); setEditOp(null); }} />}
    </div>
  );
}

// ============================================
// EDIT OP MODAL
// ============================================

function EditOPModal({ op, ops = [], onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    numeComplet: op.numeComplet || '',
    telefon: op.telefon || '',
    role: (op.role || 'op').toLowerCase(),
    csoId: op.csoId ? String(op.csoId) : '',
    reteaId: op.reteaId ? String(op.reteaId) : '',
    regiuneId: op.regiuneId ? String(op.regiuneId) : '',
    managerId: op.managerId ? String(op.managerId) : '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    if (formData.numeComplet.trim().length < 3) return 'Numele trebuie să aibă minim 3 caractere.';
    if (formData.role === 'retea' && !formData.csoId) return 'Selectează un CSO pentru rolul Rețea.';
    if (formData.role === 'regiune' && !formData.reteaId) return 'Selectează o Rețea pentru rolul Regiune.';
    if (formData.role === 'manager' && !formData.regiuneId) return 'Selectează o Regiune pentru rolul Manager.';
    if (formData.password.length > 0 && formData.password.length < 6) return 'Parola trebuie să aibă minim 6 caractere.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);

    const payload = {
      numeComplet: formData.numeComplet,
      telefon: formData.telefon || undefined,
      role: formData.role
    };
    if (formData.role === 'retea' && formData.csoId) payload.csoId = parseInt(formData.csoId, 10);
    if (formData.role === 'regiune' && formData.reteaId) {
      payload.reteaId = parseInt(formData.reteaId, 10);
      const retea = ops.find((o) => o.id === parseInt(formData.reteaId, 10));
      if (retea?.csoId) payload.csoId = retea.csoId;
    }
    if (formData.role === 'manager' && formData.regiuneId) {
      payload.regiuneId = parseInt(formData.regiuneId, 10);
      const regiune = ops.find((o) => o.id === parseInt(formData.regiuneId, 10));
      if (regiune?.reteaId) payload.reteaId = regiune.reteaId;
      if (regiune?.csoId) payload.csoId = regiune.csoId;
    }
    if (formData.password.trim()) payload.password = formData.password;

    try {
      await api.put(`/admin/ops/${op.id}`, payload);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Eroare la actualizare');
    } finally {
      setLoading(false);
    }
  };

  const csos = ops.filter((o) => (o.role || 'op') === 'cso' && o.id !== op.id);
  const retele = ops.filter((o) => (o.role || 'op') === 'retea' && o.id !== op.id);
  const regiuni = ops.filter((o) => (o.role || 'op') === 'regiune' && o.id !== op.id);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#161a28] rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Editează Rol</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rol</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value, csoId: '', reteaId: '', regiuneId: '', managerId: '' })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            >
              {OP_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          {formData.role === 'retea' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CSO</label>
              <select
                value={formData.csoId}
                onChange={(e) => setFormData({ ...formData, csoId: e.target.value })}
                className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              >
                <option value="">— Alege CSO —</option>
                {csos.map((o) => (
                  <option key={o.id} value={o.id}>{o.numeComplet}</option>
                ))}
              </select>
            </div>
          )}
          {formData.role === 'regiune' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rețea</label>
              <select
                value={formData.reteaId}
                onChange={(e) => setFormData({ ...formData, reteaId: e.target.value })}
                className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              >
                <option value="">— Alege Rețea —</option>
                {retele.map((o) => (
                  <option key={o.id} value={o.id}>{o.numeComplet}</option>
                ))}
              </select>
            </div>
          )}
          {formData.role === 'manager' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Regiune</label>
              <select
                value={formData.regiuneId}
                onChange={(e) => setFormData({ ...formData, regiuneId: e.target.value })}
                className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              >
                <option value="">— Alege Regiune —</option>
                {regiuni.map((o) => (
                  <option key={o.id} value={o.id}>{o.numeComplet}</option>
                ))}
              </select>
            </div>
          )}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nume complet</label>
            <input
              type="text"
              required
              value={formData.numeComplet}
              onChange={(e) => setFormData({ ...formData, numeComplet: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
            <input type="text" value={op.username} readOnly className="w-full border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-gray-400" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input type="text" value={op.email} readOnly className="w-full border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-gray-400" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefon (opțional)</label>
            <input
              type="tel"
              value={formData.telefon}
              onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Parolă nouă (lăsați gol pentru a păstra)</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 px-4 py-2 rounded-lg text-sm">{error}</div>
          )}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors">Anulează</button>
            <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 shadow-sm transition-all">
              {loading ? 'Se salvează...' : 'Salvează'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// ADD OP MODAL
// ============================================

const OP_ROLES = [
  { value: 'cso', label: 'CSO' },
  { value: 'retea', label: 'Rețea' },
  { value: 'regiune', label: 'Regiune' },
  { value: 'manager', label: 'Manager' },
  { value: 'op', label: 'OP' }
];

function AddOPModal({ onClose, onSuccess, ops = [] }) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    numeComplet: '',
    telefon: '',
    role: 'op',
    csoId: '',
    reteaId: '',
    regiuneId: '',
    managerId: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    if (formData.numeComplet.trim().length < 3) return 'Numele trebuie să aibă minim 3 caractere.';
    if (formData.username.trim().length < 3) return 'Username trebuie să aibă minim 3 caractere.';
    if (!/^[a-zA-Z0-9]+$/.test(formData.username)) return 'Username poate conține doar litere și cifre.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Email invalid.';
    if (formData.password.length < 6) return 'Parola trebuie să aibă minim 6 caractere.';
    if (formData.role === 'retea' && !formData.csoId) return 'Selectează un CSO pentru rolul Rețea.';
    if (formData.role === 'regiune' && !formData.reteaId) return 'Selectează o Rețea pentru rolul Regiune.';
    if (formData.role === 'manager' && !formData.regiuneId) return 'Selectează o Regiune pentru rolul Manager.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    const payload = {
      username: formData.username,
      email: formData.email,
      password: formData.password,
      numeComplet: formData.numeComplet,
      telefon: formData.telefon || undefined,
      role: formData.role
    };
    if (formData.role === 'retea' && formData.csoId) {
      payload.csoId = parseInt(formData.csoId, 10);
    }
    if (formData.role === 'regiune' && formData.reteaId) {
      payload.reteaId = parseInt(formData.reteaId, 10);
      const retea = ops.find((o) => o.id === parseInt(formData.reteaId, 10));
      if (retea?.csoId) payload.csoId = retea.csoId;
    }
    if (formData.role === 'manager' && formData.regiuneId) {
      payload.regiuneId = parseInt(formData.regiuneId, 10);
      const regiune = ops.find((o) => o.id === parseInt(formData.regiuneId, 10));
      if (regiune?.reteaId) payload.reteaId = regiune.reteaId;
      if (regiune?.csoId) payload.csoId = regiune.csoId;
    }

    try {
      await api.post('/admin/ops', payload);
      onSuccess();
      onClose();
    } catch (err) {
      const apiError = err.response?.data?.error;
      const details = err.response?.data?.details;

      if (details && Array.isArray(details)) {
        setError(details.map(d => d.msg).join(', '));
      } else {
        setError(apiError || 'Eroare la crearea rolului');
      }
    } finally {
      setLoading(false);
    }
  };

  const csos = ops.filter((op) => (op.role || 'op') === 'cso');
  const retele = ops.filter((op) => (op.role || 'op') === 'retea');
  const regiuni = ops.filter((op) => (op.role || 'op') === 'regiune');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#161a28] rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Adaugă Rol nou</h3>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rol</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value, csoId: '', reteaId: '', regiuneId: '', managerId: '' })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            >
              {OP_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {formData.role === 'retea' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CSO</label>
              <select
                value={formData.csoId}
                onChange={(e) => setFormData({ ...formData, csoId: e.target.value })}
                className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              >
                <option value="">— Alege CSO —</option>
                {csos.map((op) => (
                  <option key={op.id} value={op.id}>{op.numeComplet}</option>
                ))}
              </select>
            </div>
          )}

          {formData.role === 'regiune' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rețea</label>
              <select
                value={formData.reteaId}
                onChange={(e) => setFormData({ ...formData, reteaId: e.target.value })}
                className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              >
                <option value="">— Alege Rețea —</option>
                {retele.map((op) => (
                  <option key={op.id} value={op.id}>{op.numeComplet}</option>
                ))}
              </select>
            </div>
          )}

          {formData.role === 'manager' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Regiune</label>
              <select
                value={formData.regiuneId}
                onChange={(e) => setFormData({ ...formData, regiuneId: e.target.value })}
                className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              >
                <option value="">— Alege Regiune —</option>
                {regiuni.map((op) => (
                  <option key={op.id} value={op.id}>{op.numeComplet}</option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nume complet</label>
            <input
              type="text"
              required
              value={formData.numeComplet}
              onChange={(e) => setFormData({ ...formData, numeComplet: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
            <input
              type="text"
              required
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Parolă</label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefon (opțional)</label>
            <input
              type="tel"
              value={formData.telefon}
              onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
            >
              Anulează
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 shadow-sm transition-all"
            >
              {loading ? 'Se creează...' : 'Creează Rol'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// PLACEHOLDERS
// ============================================

function AgentiList() {
  const [agenti, setAgenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [ops, setOps] = useState([]);

  const fetchAgenti = async () => {
    try {
      const response = await api.get('/admin/agenti');
      setAgenti(response.data.agents || []);
    } catch (error) {
      console.error('Eroare încărcare agenți:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOps = async () => {
    try {
      const response = await api.get('/admin/ops');
      setOps(response.data.ops || []);
    } catch (error) {
      console.error('Eroare încărcare OP-uri:', error);
    }
  };

  useEffect(() => {
    fetchAgenti();
  }, []);

  useEffect(() => {
    if (showEditModal) fetchOps();
  }, [showEditModal]);

  const openEdit = (agent) => {
    setSelectedAgent(agent);
    setShowEditModal(true);
  };

  const openDeleteConfirm = (agent) => {
    setAgentToDelete(agent);
    setShowDeleteConfirm(true);
  };

  const handleDeleteAgent = async () => {
    if (!agentToDelete) return;
    try {
      await api.delete(`/admin/agenti/${agentToDelete.id}`);
      setAgentToDelete(null);
      setShowDeleteConfirm(false);
      fetchAgenti();
    } catch (error) {
      console.error('Eroare ștergere agent:', error);
      alert(error.response?.data?.error || 'Eroare la ștergerea agentului');
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 p-12 text-center">
        <div className="inline-block w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Se încarcă agenții...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Lista Agenți</h2>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Total: {agenti.length} agenți
        </div>
      </div>

      {agenti.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-8 text-center">
          <div className="text-6xl mb-4">👥</div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Nu există agenți în sistem încă.</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Agenții se creează de către OP-uri în dashboard-urile lor.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Agent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Telefon</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">OP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Lead-uri</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ACȚIUNI</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-slate-700">
                {agenti.map((agent) => (
                  <tr key={agent.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {agent.numeComplet}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {agent.telefon}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {agent.email || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div>{agent.op?.numeComplet}</div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">{agent.op?.email}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${agent.status === 'activ'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                        {agent.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {agent._count?.leads || 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(agent)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 border border-blue-500/30"
                          title="Editează"
                        >
                          ✎ Editează
                        </button>
                        <button
                          type="button"
                          onClick={() => openDeleteConfirm(agent)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/30"
                          title="Șterge"
                        >
                          🗑 Șterge
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showEditModal && selectedAgent && (
        <AdminEditAgentModal
          agent={selectedAgent}
          ops={ops}
          onClose={() => { setShowEditModal(false); setSelectedAgent(null); }}
          onSuccess={() => { setShowEditModal(false); setSelectedAgent(null); fetchAgenti(); }}
        />
      )}

      {showDeleteConfirm && agentToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Șterge agent</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Ești sigur că vrei să ștergi agentul <strong className="text-gray-900 dark:text-white">{agentToDelete.numeComplet}</strong>? Acțiunea este ireversibilă.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => { setShowDeleteConfirm(false); setAgentToDelete(null); }}
                className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-600"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={handleDeleteAgent}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Șterge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminEditAgentModal({ agent, ops, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    numeComplet: agent?.numeComplet || '',
    email: agent?.email || '',
    telefon: agent?.telefon || '',
    status: agent?.status || 'activ',
    opId: agent?.opId ?? agent?.op?.id ?? ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setFormData({
      numeComplet: agent?.numeComplet || '',
      email: agent?.email || '',
      telefon: agent?.telefon || '',
      status: agent?.status || 'activ',
      opId: agent?.opId ?? agent?.op?.id ?? ''
    });
  }, [agent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.put(`/admin/agenti/${agent.id}`, {
        numeComplet: formData.numeComplet.trim(),
        email: formData.email.trim(),
        telefon: formData.telefon.trim(),
        status: formData.status,
        opId: formData.opId ? parseInt(formData.opId, 10) : undefined
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Eroare la actualizare');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Editează Agent</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nume complet</label>
            <input
              type="text"
              value={formData.numeComplet}
              onChange={(e) => setFormData({ ...formData, numeComplet: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefon</label>
            <input
              type="text"
              value={formData.telefon}
              onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            >
              <option value="activ">Activ</option>
              <option value="inactiv">Inactiv</option>
              <option value="pending_consimtamant">Pending consimțământ</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">OP / Manager</label>
            <select
              value={formData.opId}
              onChange={(e) => setFormData({ ...formData, opId: e.target.value })}
              className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            >
              <option value="">— Alege OP —</option>
              {ops.map((op) => (
                <option key={op.id} value={op.id}>{op.numeComplet} ({op.email})</option>
              ))}
            </select>
          </div>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 px-4 py-2 rounded-lg text-sm">{error}</div>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-600">
              Anulează
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Se salvează...' : 'Salvează'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
function LandingPagesList() {
  const [landingPages, setLandingPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLandingPages();
  }, []);

  const fetchLandingPages = async () => {
    try {
      const response = await api.get('/admin/landing-pages');
      setLandingPages(response.data.landingPages || []);
    } catch (error) {
      console.error('Eroare încărcare landing pages:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 p-12 text-center">
        <div className="inline-block w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Se încarcă landing pages...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Lista Landing Pages</h2>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Total: {landingPages.length}
        </div>
      </div>

      {landingPages.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-8 text-center">
          <div className="text-6xl mb-4">🌐</div>
          <p className="text-gray-600 dark:text-gray-400">Nu există Landing Pages create.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700">
          <div className="overflow-x-auto w-full">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Titlu</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">URL (Slug)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">OP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Leads / Link-uri</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Acțiuni</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-slate-700">
                {landingPages.map((lp) => (
                  <tr key={lp.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{lp.titlu}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{lp.denumireLunga}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400">
                      <div className="max-w-xs truncate" title={`/lp/${lp.slug}`}>
                        <a href={`/lp/${lp.slug}`} target="_blank" rel="noopener noreferrer">
                          /lp/{lp.slug}
                        </a>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div>{lp.op?.numeComplet}</div>
                      <div className="text-xs text-gray-400">{lp.op?.email}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${lp.activ
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                        {lp.activ ? 'Activ' : 'Inactiv'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div>Leads: {lp._count?.leads || 0}</div>
                      <div className="text-xs">Links: {lp._count?.linkuriGenerate || 0}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      -
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function LeadsList() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await api.get('/admin/leads');
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Eroare încărcare lead-uri:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    try {
      const response = await api.get('/admin/leads/export', {
        responseType: 'blob', // Important pentru fișiere
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Eroare export:', error);
      alert('Eroare la exportul lead-urilor');
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 p-12 text-center">
        <div className="inline-block w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Se încarcă lead-urile...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Lista Lead-uri</h2>
        <div className="flex gap-4 items-center">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Total: {leads.length}
          </div>
          <button
            onClick={exportExcel}
            className="bg-green-600 text-white px-4 py-2.5 rounded-xl font-medium shadow-sm hover:bg-green-700 hover:shadow transition-all text-sm"
          >
            📥 Export Excel
          </button>
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-8 text-center">
          <div className="text-6xl mb-4">📝</div>
          <p className="text-gray-600 dark:text-gray-400">Nu există lead-uri în sistem.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nume / Telefon</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Landing Page</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">OP / Agent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">DPA</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acțiuni</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-slate-700">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{lead.nume || '-'}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{lead.telefon}</div>
                      <div className="text-xs text-gray-400">{lead.email}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {lead.landingPage?.titlu}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div>OP: {lead.op?.numeComplet}</div>
                      <div className="text-xs">Agent: {lead.agent?.numeComplet}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {lead.telefonValidat ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          Validat SMS
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                          Nevalidat
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(lead.dataCompletare).toLocaleString('ro-RO')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}